import PropTypes from 'prop-types';
import Tooltip from '../Tooltip.jsx';
import SanitizedHtml from '../SanitizedHtml.jsx';
import {
  ASSESSMENT_COLORS,
  getAssessmentColor,
  getAssessmentLabel,
  getAssessmentProvenanceLabel,
  normalizeAssessmentColor,
  normalizeAssessments,
} from '../../utils/assessmentPresentation.js';

const ITEM_TYPE_LABELS = {
  metabolite: 'Metabólito',
  therapeuticEffect: 'Efeito terapêutico',
};

/**
 * Resume as avaliações de uma associação e abre o painel completo sob demanda.
 *
 * @param {object} props - Propriedades da associação apresentada
 * @returns {JSX.Element} Tooltip interativo ancorado ao chip
 */
const EvidenceTooltip = ({
  children,
  title,
  description = '',
  itemType,
  assessments = [],
  relevance = null,
  onOpenDetails,
}) => {
  const normalizedAssessments = normalizeAssessments(assessments, relevance, itemType);
  const itemTypeLabel = ITEM_TYPE_LABELS[itemType];

  const openDetails = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onOpenDetails();
  };

  return (
    <Tooltip
      trigger="hover"
      position="top"
      interactive
      contentClassName="w-[min(23rem,calc(100vw-1.25rem))] max-w-[calc(100vw-1.25rem)]"
      content={
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {itemTypeLabel}
            </p>
            <p className="font-semibold leading-snug">{title}</p>
            {description && (
              <SanitizedHtml
                html={description}
                className="mt-1 text-xs leading-relaxed text-gray-700 dark:text-gray-200 [&_p]:m-0"
              />
            )}
          </div>

          <div className="space-y-2 border-t border-gray-200 pt-3 dark:border-gray-700">
            {normalizedAssessments.length > 0 ? normalizedAssessments.map((assessment) => {
              const provenanceLabel = getAssessmentProvenanceLabel(assessment);
              return (
                <div
                  key={assessment.id || `${assessment.dimension?.code}:${assessment.value?.code}`}
                  className="flex items-start gap-2"
                >
                  <span
                    className="mt-1 h-3 w-3 flex-shrink-0 rounded-full border border-black/10 dark:border-white/20"
                    style={{ backgroundColor: getAssessmentColor(assessment) }}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold leading-snug">
                      {assessment.dimension?.name || 'Avaliação'}
                    </p>
                    <p className="text-xs leading-snug text-gray-700 dark:text-gray-200">
                      {getAssessmentLabel(assessment)}
                      {assessment.dimension?.framework?.name
                        ? ` · ${assessment.dimension.framework.name}`
                        : ''}
                    </p>
                    {provenanceLabel && (
                      <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                        {provenanceLabel}
                      </p>
                    )}
                  </div>
                </div>
              );
            }) : (
              <div className="flex items-center gap-2">
                <span
                  className="h-3 w-3 rounded-full border border-black/10 dark:border-white/20"
                  style={{
                    backgroundColor: normalizeAssessmentColor(ASSESSMENT_COLORS.UNKNOWN),
                  }}
                  aria-hidden="true"
                />
                <p className="text-xs text-gray-700 dark:text-gray-200">
                  Avaliação não cadastrada
                </p>
              </div>
            )}
          </div>

          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={openDetails}
            className="text-xs font-semibold text-greenbook-light-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 dark:text-greenbook-dark-primary"
          >
            Mais detalhes
          </button>
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};

EvidenceTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  itemType: PropTypes.oneOf(['metabolite', 'therapeuticEffect']).isRequired,
  assessments: PropTypes.arrayOf(PropTypes.object),
  relevance: PropTypes.object,
  onOpenDetails: PropTypes.func.isRequired,
};

export default EvidenceTooltip;
